import { useEffect, useState } from "react";
import { doc, getDoc, addDoc, updateDoc, collection, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebase";
import { S, C } from "../ui";
import ClockInOut from "./ClockInOut";
import ChecklistRenderer from "./ChecklistRenderer";
import BeforeAfterCapture from "./BeforeAfterCapture";

const CLOCK_KEY = (workerId) => `edifice_clock_${workerId}`;

export async function uploadFile(path, file) {
  const r = ref(storage, path);
  await uploadBytes(r, file);
  return getDownloadURL(r);
}

// The actual checklist/clock-in screen — extracted out of FieldFlow.jsx
// (Phase 11) so it can be reached two ways: a QR scan, which already
// knows the exact assetId (FieldFlow.jsx keeps that path unchanged), or
// the new Staff Home "Check In" tab, where a logged-in staff session
// picks an asset themselves via AssetPicker.jsx. Takes an
// already-PIN-verified `worker` — this component has no login concerns
// of its own, purely "given a worker and an asset, run the check-in."
export default function AssetCheckIn({ facilityId, assetId, worker }) {
  const [asset, setAsset] = useState(undefined); // undefined = loading, null = doesn't exist
  const [template, setTemplate] = useState(null);
  const [openEntry, setOpenEntry] = useState(null);
  const [checklistDone, setChecklistDone] = useState({});
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    getDoc(doc(db, "assets", assetId)).then(s => setAsset(s.exists() ? { id: s.id, ...s.data() } : null));
  }, [assetId]);

  useEffect(() => {
    if (!asset) return;
    const overrideId = `${facilityId}-${asset.trade.toLowerCase()}`;
    const globalId = `global-${asset.trade.toLowerCase()}`;
    getDoc(doc(db, "checklistTemplates", overrideId)).then(s => {
      if (s.exists()) { setTemplate({ id: s.id, ...s.data() }); return; }
      getDoc(doc(db, "checklistTemplates", globalId)).then(s2 => {
        setTemplate(s2.exists() ? { id: s2.id, ...s2.data() } : { id: null, items: [] });
      });
    });
  }, [asset, facilityId]);

  useEffect(() => {
    const raw = localStorage.getItem(CLOCK_KEY(worker.id));
    if (raw) { try { setOpenEntry(JSON.parse(raw)); } catch { /* ignore */ } }
  }, [worker.id]);

  const clockIn = async (file) => {
    const url = await uploadFile(`timeCardPhotos/${facilityId}/${worker.id}_${Date.now()}_in`, file);
    const ref2 = await addDoc(collection(db, "timeCardEntries"), {
      facilityId, vendorId: worker.vendorId || null, workerId: worker.id,
      clockInAt: serverTimestamp(), clockInPhotoUrl: url,
      clockOutAt: null, clockOutPhotoUrl: null, status: "open",
    });
    const entry = { id: ref2.id, clockInLabel: new Date().toLocaleTimeString() };
    localStorage.setItem(CLOCK_KEY(worker.id), JSON.stringify(entry));
    setOpenEntry(entry);
  };

  const clockOut = async (file) => {
    const url = await uploadFile(`timeCardPhotos/${facilityId}/${worker.id}_${Date.now()}_out`, file);
    await updateDoc(doc(db, "timeCardEntries", openEntry.id), {
      clockOutAt: serverTimestamp(), clockOutPhotoUrl: url, status: "closed",
    });
    localStorage.removeItem(CLOCK_KEY(worker.id));
    setOpenEntry(null);
  };

  const finishChecklist = async ({ beforeUrls, afterUrls }) => {
    await addDoc(collection(db, "checklistCompletions"), {
      facilityId, vendorId: worker.vendorId || null, workerId: worker.id,
      assetId, templateId: template?.id || null,
      items: (template?.items || []).map(text => ({ text, done: !!checklistDone[text] })),
      photoUrls: { before: beforeUrls, after: afterUrls },
      timestamp: serverTimestamp(), status: "complete",
    });
    setSaved(true);
  };

  if (asset === undefined) return <div style={{ textAlign: "center", color: C.muted }}>Loading…</div>;
  if (asset === null) return <div style={{ textAlign: "center", color: C.red }}>This asset doesn't exist.</div>;

  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.accent, letterSpacing: ".08em", textTransform: "uppercase", marginBottom: 4 }}>{asset.trade} · {worker.name}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: C.navy, marginBottom: 16 }}>{asset.name}</div>

      <ClockInOut openEntry={openEntry} onClockIn={clockIn} onClockOut={clockOut} />

      {!template || template.items.length === 0 ? (
        <div style={{ marginTop: 16, fontSize: 13, color: C.muted }}>No checklist template set for {asset.trade} yet — ask your facility admin.</div>
      ) : saved ? (
        <div style={{ marginTop: 16, padding: 16, background: "#F0FBF4", border: `1px solid ${C.green}55`, borderRadius: 10, color: C.green, fontWeight: 600, textAlign: "center" }}>
          ✓ Documentation saved for {asset.name}
        </div>
      ) : (<>
        <div style={{ marginTop: 20, marginBottom: 8, fontSize: 12, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5 }}>Checklist</div>
        <ChecklistRenderer
          items={template.items.map(text => ({ id: text, text, done: !!checklistDone[text] }))}
          canEdit={false}
          onToggle={(item) => setChecklistDone(prev => ({ ...prev, [item.text]: !prev[item.text] }))}
        />
        <div style={{ marginTop: 20 }}>
          <BeforeAfterCapture
            title={asset.name}
            subtitle={`${asset.trade} · check off items above, then document with photos`}
            onCaptureBefore={(file) => uploadFile(`checklistPhotos/${facilityId}/${assetId}/${Date.now()}_before`, file)}
            onCaptureAfter={(file) => uploadFile(`checklistPhotos/${facilityId}/${assetId}/${Date.now()}_after`, file)}
            onFinish={finishChecklist}
          />
        </div>
      </>)}
    </div>
  );
}
